import { StyledConfigRow, StyledIconButton, StyledIconRow } from "./StyledComponents";
import {
    Embed24Icon,
    Facebook24Icon,
    LineSocial24Icon,
    Link24Icon,
    LinkedIn24Icon,
    Mail24Icon,
    Pinterest24Icon,
    Twitter24Icon,
} from "@kaltura/ds-react-icons";
import { Tooltip } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { parseTime, stringifyTime } from "../helper";
import TimeSection from "../time-section/TimeSection";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { Analytics, ButtonClickAnalyticsType, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { CopyToClipboardInput, CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { CustomShareItem } from "../ShareModal";

export type LinksType = ("facebook" | "twitter" | "linkedin" | "line" | "pinterest" | "email" | "url")[];

export interface ShareSectionProps {
    title: string;
    setShowEmbed: (val: boolean) => void;
    setShowOEmbed: (val: boolean) => void;
    showEmbeds: boolean;
    hideEmbed: boolean;
    oEmbed: boolean;
    entryUrl: string;
    isTimingAllowed: boolean;
    links: LinksType;
    copyToClipboardText: string;
    analyticsData?: Analytics;
    customShareItems?: CustomShareItem[];
    customShareSelected?: (component: KmsTypeComponent<any, { onClose: () => void }>) => void;
}

/**
 * modal share section
 */
const ShareSection = (props: ShareSectionProps) => {
    const {
        title,
        setShowEmbed,
        setShowOEmbed,
        showEmbeds,
        hideEmbed = false,
        oEmbed = false,
        entryUrl,
        isTimingAllowed,
        links,
        copyToClipboardText,
        analyticsData,
        customShareItems = [],
        customShareSelected,
    } = props;

    const [displayStartTime, setDisplayStartTime] = useState<string>(stringifyTime(0));

    const startTime = parseTime(displayStartTime);
    let url;
    if (entryUrl.includes("?")) {
        url = `${entryUrl}${startTime !== 0 ? "&st=" + startTime : ""}`;
    }
    else {
        url = `${entryUrl}${startTime !== 0 ? "?st=" + startTime : ""}`;
    }

    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);

    const { showToast } = useToastsContext();

    const updateStartTime = (startTime: number) => {
        setDisplayStartTime(stringifyTime(startTime));
    };

    const sendButtonAnalytics = useButtonAnalytics();

    const onEmbedClick = () => {
        setShowEmbed(true);
        sendAnalytics("Embed", ButtonClickAnalyticsType.OPEN);
    };

    const onOembedClick = () => {
        setShowOEmbed(true);
        sendAnalytics("oEmbed", ButtonClickAnalyticsType.OPEN);
    };

    const onCopyButtonClick = () => {
        showToast({
            severity: "success",
            message: translate("Link copied to clipboard."),
        });
        sendAnalytics("Copy Share Link", ButtonClickAnalyticsType.SHARE);
    };

    const sendAnalytics = (action: string, buttonType: number) => {
        sendButtonAnalytics(`Share - ${action}`, buttonType, analyticsData?.mediaId);
    };

    const className = "kms-ds-share-modal-share-section";

    return (
        <div className={className}>
            <StyledIconRow>
                {links.includes("facebook") && (
                    <Tooltip title={translate("Share to Facebook")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share to Facebook")}
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                            target="_blank"
                            className={className + "-button"}
                        >
                            <Facebook24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}
                {links.includes("twitter") && (
                    <Tooltip title={translate("Share to X")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share to X")}
                            href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
                            target="_blank"
                            className={"twitter-share-button " + className + "-button"}
                        >
                            <Twitter24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}
                {links.includes("linkedin") && (
                    <Tooltip title={translate("Share to LinkedIn")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share to LinkedIn")}
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                            target="_blank"
                            className={className + "-button"}
                        >
                            <LinkedIn24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {links.includes("line") && (
                    <Tooltip title={translate("Share to Line")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share to Line")}
                            href={`https://social-plugins.line.me/lineit/share?url=${encodedUrl}`}
                            target="_blank"
                            className={className + "-button"}
                        >
                            <LineSocial24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {links.includes("pinterest") && (
                    <Tooltip title={translate("Share to Pinterest")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share to Pinterest")}
                            href={`https://pinterest.com/pin/create/link/?url=${encodedUrl}`}
                            target="_blank"
                            className={className + "-button"}
                        >
                            <Pinterest24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {links.includes("email") && (
                    <Tooltip title={translate("Share with email")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Share with email")}
                            href={`mailto:email@example.com?subject=${encodedTitle}&body=${translate("check out %1", [encodedUrl])}`}
                            target="_blank"
                            onClick={() => sendAnalytics("Email", ButtonClickAnalyticsType.SHARE)}
                            className={className + "-button"}
                        >
                            <Mail24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {showEmbeds && !hideEmbed && (
                    <Tooltip title={translate("Embed")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("Embed")}
                            onClick={onEmbedClick}
                            className={className + "-button"}
                        >
                            <Embed24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {showEmbeds && oEmbed && (
                    <Tooltip title={translate("oEmbed")} placement="top">
                        <StyledIconButton
                            variant={"pill"}
                            color={"secondary"}
                            aria-label={translate("oEmbed")}
                            onClick={onOembedClick}
                            className={className + "-button"}
                        >
                            <Link24Icon />
                        </StyledIconButton>
                    </Tooltip>
                )}

                {customShareItems?.map((item, index) => (
                    <Tooltip key={index} title={item.tooltip} placement="top">
                        <StyledIconButton
                            variant="pill"
                            color="secondary"
                            aria-label={item.tooltip}
                            onClick={() => customShareSelected?.(item.component)}
                            className={className + "-button"}
                        >
                            <CustomComponent component={item.icon} />
                        </StyledIconButton>
                    </Tooltip>
                ))}
            </StyledIconRow>

            {links.includes("url") && (
                <>
                    <CopyToClipboardInput
                        text={url}
                        label={copyToClipboardText}
                        onCopy={onCopyButtonClick}
                        className={`${className}-copy-to-clipboard`}
                    />
                    {isTimingAllowed && (
                        <StyledConfigRow>
                            <TimeSection onTimeUpdate={updateStartTime} />
                        </StyledConfigRow>
                    )}
                </>
            )}
        </div>
    );
};

export default ShareSection;
